/**
 * A picture of the countdown, drawn in CSS (2026-10-30 intro sheet): the
 * iPhone Lock Screen card with its Dynamic Island, or the Android ongoing
 * notification, for the platform the trainer is on. Shows the lead they are
 * choosing, so "15 minutes" reads as a timer at 15:00, not as a sentence.
 *
 * Pikachu's official art, the same picture the real card parks in the App
 * Group (lib/liveActivities cacheEventSprite).
 */
import { officialArtworkUrl } from "@shared/spriteCdn";
import type { LiveEventDelivery, LiveEventLeadMinutes } from "@/lib/liveEventPrefs";

function timerFor(lead: LiveEventLeadMinutes): { digits: string; caption: string; phase: string } {
  if (lead === 0) return { digits: "59:59", caption: "left", phase: "ON NOW" };
  if (lead === 60) return { digits: "59:59", caption: "until start", phase: "SOON" };
  return { digits: `${String(lead - 1).padStart(2, "0")}:59`, caption: "until start", phase: "SOON" };
}

const GREEN = "hsl(150 70% 50%)";

export function LiveEventCountdownMock({
  platform,
  lead,
  delivery,
}: {
  platform: "ios" | "android" | "web";
  lead: LiveEventLeadMinutes;
  delivery: LiveEventDelivery;
}) {
  const t = timerFor(lead);
  const art = officialArtworkUrl(25, { shiny: true });
  const notificationOnly = delivery === "push" || platform === "web";

  if (notificationOnly) {
    // One banner, the plain notification, on either phone.
    return (
      <div
        data-testid="live-events-mock-notification"
        className="rounded-2xl p-3 flex items-center gap-3"
        style={{ background: "hsl(225 20% 12%)", border: "1px solid hsl(225 15% 22%)", color: "hsl(0 0% 96%)" }}
      >
        <img src={art} alt="" className="w-10 h-10 object-contain shrink-0" loading="lazy" />
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: "hsl(225 10% 60%)" }}>
            Nexus Dex · now
          </p>
          <p className="text-[12px] font-bold leading-tight truncate">
            {lead === 0 ? "Pikachu Spotlight Hour is live" : `Pikachu Spotlight Hour starts in ${lead === 60 ? "1 hour" : `${lead} minutes`}`}
          </p>
          <p className="text-[10px] leading-tight truncate" style={{ color: "hsl(225 10% 70%)" }}>
            2× Catch Stardust. Open Pokémon GO.
          </p>
        </div>
      </div>
    );
  }

  if (platform === "android") {
    return (
      <div
        data-testid="live-events-mock-android"
        className="rounded-2xl p-3"
        style={{ background: "hsl(225 20% 12%)", border: "1px solid hsl(225 15% 22%)", color: "hsl(0 0% 96%)" }}
      >
        <p className="text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: "hsl(225 10% 60%)" }}>
          Nexus Dex · Spotlight Hour
        </p>
        <div className="mt-1 flex items-center gap-3">
          <img src={art} alt="" className="w-12 h-12 object-contain shrink-0" loading="lazy" />
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-black leading-tight" style={{ color: GREEN }}>
              Pikachu
            </p>
            <p className="text-[10px] font-semibold" style={{ color: "hsl(225 10% 70%)" }}>
              2× Catch Stardust · <span style={{ color: "hsl(45 95% 60%)" }}>SHINY</span>
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[9px] font-black tracking-[0.14em]" style={{ color: t.phase === "ON NOW" ? GREEN : "hsl(225 10% 60%)" }}>
              {t.phase}
            </p>
            <p className="text-[22px] font-black leading-none tabular-nums">{t.digits}</p>
            <p className="text-[9px]" style={{ color: "hsl(225 10% 60%)" }}>
              {t.caption}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // iPhone: the Dynamic Island above, the Lock Screen card below.
  return (
    <div data-testid="live-events-mock-ios" className="space-y-2">
      <div className="flex justify-center">
        <div
          className="inline-flex items-center gap-2 rounded-full px-3 py-1.5"
          style={{ background: "hsl(0 0% 4%)", border: "1px solid hsl(0 0% 18%)", color: "hsl(0 0% 96%)" }}
          aria-label="Dynamic Island"
        >
          <img src={art} alt="" className="w-5 h-5 object-contain" loading="lazy" />
          <span className="text-[11px] font-black tabular-nums" style={{ color: GREEN }}>
            {t.digits}
          </span>
        </div>
      </div>
      <div
        className="rounded-2xl p-3"
        style={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 18%)", color: "hsl(0 0% 96%)" }}
      >
        <div className="flex items-center gap-3">
          <img src={art} alt="" className="w-14 h-14 object-contain shrink-0" loading="lazy" />
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: "hsl(0 0% 60%)" }}>
              Spotlight Hour
            </p>
            <p className="text-[17px] font-black leading-tight" style={{ color: GREEN }}>
              Pikachu
            </p>
            <p className="text-[10px] font-semibold" style={{ color: "hsl(0 0% 70%)" }}>
              2× Catch Stardust · <span style={{ color: "hsl(45 95% 60%)" }}>SHINY</span>
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[9px] font-black tracking-[0.14em]" style={{ color: t.phase === "ON NOW" ? GREEN : "hsl(0 0% 60%)" }}>
              {t.phase}
            </p>
            <p className="text-[24px] font-black leading-none tabular-nums">{t.digits}</p>
            <p className="text-[9px]" style={{ color: "hsl(0 0% 60%)" }}>
              {t.caption}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
